
import { useEffect, useMemo, useState, useCallback } from "react";
import { Col, Form, Input, Row, Modal } from "antd";
import { Body } from "../../../components/main-body/body";
import { useTranslation } from "react-i18next";
import useNotification from "../../../components/notification/notification";
import "./project-management.css";
import { useDispatch, useSelector } from "react-redux";
import { fetchSettings } from "../../../store/slices/settings";
import { selectCurrentProject, setCurrentProject } from "../../../store/slices/projects";
import { selectIsAdmin } from "../../../store/slices/user";
import { useCan } from "../../../common/utils/usePermissions";
import ApiProjects from "../../../api/projects";
import ProjectKeyColorPicker from "../../../components/color-picker/project-key-color-picker";
import { normalizeCode, makeCodeFromTitle } from "../../../common/utils/projectCode";
import { useProjectNavigate } from "../../../common/utils/useProjectNavigate";
import { normalizeHex } from "../../../common/utils/color";
import useAdminEvents from "../../../hooks/useAdminEvents";
import { refreshProjects } from "../../../store/thunks/projects";

const DeleteModalComponent = ({ title, isModalOpen, handleOk, handleCancel }) => {
  return (
    <Modal
      title={title}
      open={isModalOpen}
      onOk={handleOk}
      onCancel={handleCancel}
      okButtonProps={{ danger: true }}
      okText="Delete"
      className="plan-page-action-modal"
    >
      <p>Are you sure you want to delete this project?</p>
    </Modal>
  );
};

const ProjectManagement = () => {
  const navigate = useProjectNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { openNotification } = useNotification();

  const [isCodeManual, setIsCodeManual] = useState(true);

  const currentProject = useSelector(selectCurrentProject);
  // Saving needs `settings.general`; deleting a project stays an Organization
  // admin action on the backend (LF360-2024).
  const canEdit = useCan("settings.general");
  const isAdmin = useSelector(selectIsAdmin);
  const [form] = Form.useForm();

  const titleWatch = Form.useWatch("title", form);
  const codeWatch = Form.useWatch("code", form);
  const descWatch = Form.useWatch("description", form);

  useEffect(() => {
    if (!isCodeManual) {
      const auto = makeCodeFromTitle(titleWatch || "");
      form.setFieldsValue({ code: auto });
    }
  }, [isCodeManual, titleWatch, form]);

  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [keyColor, setKeyColor] = useState(currentProject?.color || "#A3F1CB");

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const openDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(true);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(false);
  }, []);

  const handleDeleteProject = useCallback(async () => {
    try {
      if (!currentProject?.id) return;

      await ApiProjects.deleteProject(currentProject.id);

      openNotification({
        type: "success",
        message: "Project has been deleted",
      });

      setIsDeleteModalOpen(false);
      navigate("/dashboard")
    } catch (e) {
        console.error("Failed to delete project:", e);

        const err = e?.response?.data?.error;
        let msg = "Failed to delete project";

        if (err?.code === "PROJECT_HAS_SUBJECTS") {
          msg = "You cannot delete this project because it has subjects assigned.";
        } else if (typeof err?.message === "string") {
          msg = err.message;
        }

        openNotification({
          type: "error",
          message: msg,
        });
      }
  }, [currentProject?.id, openNotification]);

  useEffect(() => {
    if (currentProject) {
      form.setFieldsValue({
        title: currentProject.title || "",
        code: currentProject.code || "",
        description: currentProject.description || "",
      });
      setKeyColor(currentProject.color || "#A3F1CB");
    }
  }, [currentProject, form]);

  useAdminEvents({
    onProjectChanged: (payload) => {
      dispatch(refreshProjects());

      const changedIds = [
        ...(payload?.deletedIds || []),
        payload?.id,
      ].filter(Boolean);

      if (!currentProject?.id || !changedIds.includes(currentProject.id)) {
        return;
      }

      if (payload?.action === "deleted" || payload?.action === "bulk_deleted") {
        dispatch(setCurrentProject(null));
        setIsEditing(false);
        setIsDeleteModalOpen(false);
        navigate("/dashboard");
        return;
      }

      if (payload?.project) {
        dispatch(setCurrentProject(payload.project));
      }
    },
  });

  // const normalizeCode = useCallback((val) => {
  //   if (!val) return "";
  //   return String(val).toUpperCase().slice(0, 3);
  // }, []);

  const onToggleEdit = useCallback(() => {
    if (!isEditing) {
      form.setFieldsValue({
        title: currentProject?.title || "",
        code: currentProject?.code || "",
        description: currentProject?.description || "",
      });
      setKeyColor(currentProject?.color || "#A3F1CB");
    }
    setIsEditing((s) => !s);
  }, [isEditing, form, currentProject]);

  const onCancelEdit = useCallback(() => {
    form.setFieldsValue({
      title: currentProject?.title || "",
      code: currentProject?.code || "",
      description: currentProject?.description || "",
    });
    setKeyColor(currentProject?.color || "#A3F1CB");
    setIsEditing(false);
  }, [form, currentProject]);

  const onSave = useCallback(async () => {
    try {
      if (!currentProject?.id) return;

      const values = await form.validateFields();
      const payload = {
        title: values.title?.trim(),
        code: normalizeCode(values.code),
        description: values.description ?? "",
        color: normalizeHex(keyColor) || "#A3F1CB",
      };

      setSaving(true);
      const { data } = await ApiProjects.update(currentProject.id, payload);
      const updated = data?.project || data;
      dispatch(setCurrentProject(updated));

      openNotification({ type: "success", message: "Project has been updated" });
      setIsEditing(false);
      dispatch(fetchSettings());
    } catch (e) {
      console.error("Failed to update project:", e);

      const msg =
        e?.response?.data?.error ||
        e?.message ||
        "Failed to update project";
      openNotification({ type: "error", message: msg })
    } finally {
      setSaving(false);
    }
  }, [currentProject?.id, form, dispatch, normalizeCode, openNotification, t, keyColor]);

  const hasDiff = useMemo(() => {
    if (!currentProject) return false;
    const t0 = (currentProject.title || "").trim();
    const c0 = (currentProject.code || "").trim();
    const d0 = currentProject.description || "";
    const col0 = normalizeHex(currentProject.color || "#A3F1CB");
    const t1 = (titleWatch || "").trim();
    const c1 = normalizeCode(codeWatch || "");
    const d1 = descWatch || "";
    const col1 = normalizeHex(keyColor || "#A3F1CB");
    return t0 !== t1 || c0 !== c1 || d0 !== d1 || col0 !== col1;
  }, [currentProject, titleWatch, codeWatch, descWatch, normalizeCode, keyColor]);

  const isTitleValid = (titleWatch || "").trim().length > 0;
  const codeNorm = normalizeCode(codeWatch || "");
  const isCodeValid = codeNorm.length > 0 && codeNorm.length <= 3;
  const saveDisabled = !isEditing || saving || !hasDiff || !isTitleValid || !isCodeValid;

  const menuItems = useMemo(() => {
    if (!currentProject?.id || !isAdmin) return [];

    if (currentProject.isDefault) {
      return [];
    }

    return [
      {
        key: "delete_project",
        label: "Delete project",
        danger: true,
        onClick: () => {
          openDeleteModal();
        },
      },
    ];
  }, [currentProject?.id, isAdmin, openDeleteModal]);

  const headerConfig = useMemo(
    () => ({
      title: t("project-management.title") || "Project",
      editable: canEdit,
      isEditing,
      onToggleEdit,
      onSave,
      onCancelEdit,
      saveDisabled,
      menuItems,
    }),
    [t, canEdit, isEditing, onToggleEdit, onSave, onCancelEdit, saveDisabled, menuItems]
  );

  const readOnly = !isEditing || saving;
  // const codeText = (currentProject?.code || "").toUpperCase().slice(0, 3) || "PRJ";

  return (
    <Body header={headerConfig}>
      <div className="project-mgmt__container">
        <Form
          key={currentProject?.id || 'project-form'}
          form={form}
          layout="vertical"
          initialValues={{
            title: currentProject?.title || "",
            code: currentProject?.code || "",
            description: currentProject?.description || "",
          }}
          onValuesChange={(changed) => {
            if ("code" in changed) {
              setIsCodeManual(true);
              const v = normalizeCode(changed.code);
              form.setFieldsValue({ code: v });
            }
          }}
        >
          <Row gutter={16}>
            <Col xs={24} md={16}>
              <Form.Item
                label={t("project-management.name") || "Project name"}
                name="title"
                rules={[
                  { required: true, message: t("common.required") || "Required" },
                  { max: 255, message: t("common.too_long") || "Too long" },
                ]}
              >
                <Input placeholder={t("project-management.name_ph")} readOnly={readOnly} />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item
                label={t("project-management.code") || "Project ID"}
                name="code"
                rules={[
                  { required: true, message: t("common.required") || "Required" },
                  { max: 3, message: "Max 3 chars" },
                ]}
              >
                <Input
                  placeholder={t("project-management.code_ph")}
                  maxLength={3}
                  readOnly={readOnly}
                  onChange={(e) =>
                    form.setFieldsValue({ code: normalizeCode(e.target.value) })
                  }
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label={t("project-management.description") || "Project description"}
            name="description"
            rules={[{ max: 5000, message: "Too long" }]}
            style={{ width: "100%" }}
          >
            <Input.TextArea rows={6} placeholder={t("project-management.description_ph")} allowClear readOnly={readOnly} />
          </Form.Item>

          <Form.Item
            label={t("project-management.key_color") || "Key color"}
            style={{ width: "100%" }}
          >
            <ProjectKeyColorPicker
              value={keyColor}
              onChange={setKeyColor}
              codeText={(form.getFieldValue("code") || (currentProject?.code || "PRJ")).toUpperCase().slice(0,3)}
              disabled={!isEditing || saving}
            />
          </Form.Item>
        </Form>
      </div>
      <DeleteModalComponent
        title={currentProject?.title || "Project"}
        isModalOpen={isDeleteModalOpen}
        handleOk={handleDeleteProject}
        handleCancel={closeDeleteModal}
      />
    </Body>
  );
};

export default ProjectManagement;
